import { useEffect, useRef, useState } from "react";
import {
  Bell,
  Search,
  ChevronDown,
  Sun,
  Moon,
  Check,
  LogOut,
  Settings2,
} from "lucide-react";
import { useRouter } from "@tanstack/react-router";
import { getSession, logout } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { allowedRoles, useRole } from "@/lib/roles";
import { cn } from "@/lib/utils";
import { inputGlowWrap, inputGlowInner } from "@/lib/card-styles";
import { ScopeBar } from "./ScopeBar";
import { BuildingManagerOverlay } from "./BuildingManagerOverlay";

export function UserTopbar() {
  const router = useRouter();
  const session = getSession();
  const { palette, mode, font, setPalette, setMode, setFont } = useTheme();
  const { role, config, setRole } = useRole();
  const [menuOpen, setMenuOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const initials = session?.email?.[0]?.toUpperCase() ?? "?";
  const displayName = session?.email?.split("@")[0] ?? "—";
  const RoleIcon = config.icon;

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [menuOpen]);

  async function handleLogout() {
    await logout();
    await router.navigate({ to: "/login" });
  }

  return (
    <>
      <header className="border-b border-border bg-background/60 backdrop-blur-md sticky top-0 z-30">
        <div className="flex items-start justify-between gap-3 px-6 py-2.5">
          <div className="min-w-0 flex-1">
            <ScopeBar />
          </div>

          <div className="flex items-center gap-2 shrink-0">
          <div className={cn("hidden md:block w-72", inputGlowWrap)}>
            <div
              className={cn(
                "flex items-center gap-2 px-2.5 py-1.5",
                inputGlowInner,
              )}
            >
              <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <input
                placeholder="Hae kiinteistöä, mittaria, suositusta…"
                className="bg-transparent outline-none flex-1 text-xs placeholder:text-muted-foreground/70 text-foreground"
              />
              <kbd className="font-mono text-[10px] bg-background/60 px-1.5 py-0.5 rounded border border-border">
                /
              </kbd>
            </div>
          </div>

          <button className="relative h-8 w-8 grid place-items-center rounded-md border border-border bg-surface text-muted-foreground hover:text-foreground">
            <Bell className="h-4 w-4" />
            <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-warning" />
          </button>

          <div className="flex items-center gap-1 rounded-md border border-border bg-surface px-2 h-8">
            <button
              title="Kairos-teema"
              onClick={() => setPalette("kairos")}
              className={cn(
                "h-4 w-4 rounded-full transition-all bg-[oklch(0.58_0.24_295)]",
                palette === "kairos"
                  ? "ring-2 ring-offset-1 ring-offset-surface ring-[oklch(0.58_0.24_295)]"
                  : "opacity-40 hover:opacity-70",
              )}
            />
            <button
              title="BKRY-teema"
              onClick={() => setPalette("bkry")}
              className={cn(
                "h-4 w-4 rounded-full transition-all bg-[oklch(0.55_0.10_195)]",
                palette === "bkry"
                  ? "ring-2 ring-offset-1 ring-offset-surface ring-[oklch(0.55_0.10_195)]"
                  : "opacity-40 hover:opacity-70",
              )}
            />
            <div className="w-px h-4 bg-border mx-0.5" />
            <button
              title="Rubik"
              onClick={() => setFont("rubik")}
              className={cn(
                "px-1.5 py-0.5 rounded text-[11px] font-medium transition-colors font-[Rubik,system-ui,sans-serif]",
                font === "rubik"
                  ? "bg-primary/15 text-primary"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              Aa
            </button>
            <button
              title="Roboto"
              onClick={() => setFont("roboto")}
              className={cn(
                "px-1.5 py-0.5 rounded text-[11px] font-medium transition-colors font-[Roboto,system-ui,sans-serif]",
                font === "roboto"
                  ? "bg-primary/15 text-primary"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              Aa
            </button>
            <div className="w-px h-4 bg-border mx-0.5" />
            <button
              title={mode === "light" ? "Vaihda tummaan" : "Vaihda vaaleaan"}
              onClick={() => setMode(mode === "light" ? "dark" : "light")}
              className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            >
              {mode === "light" ? (
                <Moon className="h-3.5 w-3.5" />
              ) : (
                <Sun className="h-3.5 w-3.5" />
              )}
            </button>
          </div>

          <div ref={menuRef} className="relative">
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="flex items-center gap-2 rounded-md border border-border bg-surface px-2 py-1 text-xs hover:border-primary/40 transition-colors"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <div className="h-6 w-6 rounded-full bg-gradient-accent grid place-items-center text-primary-foreground font-medium">
                {initials}
              </div>
              <span className="hidden sm:flex flex-col items-start leading-tight">
                <span>{displayName}</span>
                <span className={`text-[10px] ${config.accent}`}>
                  {config.shortLabel}
                </span>
              </span>
              <ChevronDown
                className={`h-3 w-3 text-muted-foreground transition-transform ${menuOpen ? "rotate-180" : ""}`}
              />
            </button>

            {menuOpen && (
              <div
                role="menu"
                className="absolute right-0 mt-1 w-72 rounded-md border border-border bg-card shadow-elevated overflow-hidden z-50"
              >
                <div className="px-3 py-2 border-b border-border/60">
                  <div className="text-xs font-medium">{displayName}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {session?.email ?? "—"}
                  </div>
                </div>

                <div className="px-3 py-2 border-b border-border/60">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span
                      className={`h-5 w-5 rounded-md bg-surface-elevated grid place-items-center ${config.accent}`}
                    >
                      <RoleIcon className="h-3 w-3" />
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                      Vaihda roolia
                    </span>
                  </div>
                  <div className="text-[10px] text-muted-foreground/80 mb-2 leading-snug">
                    Rooli määrittää mitä toimintoja voit tehdä. Näkymän valitset
                    perspektiiveistä.
                  </div>
                  <div className="space-y-0.5">
                    {allowedRoles().map((r) => {
                      const Icon = r.icon;
                      const active = r.id === role;
                      return (
                        <button
                          key={r.id}
                          role="menuitemradio"
                          aria-checked={active}
                          onClick={() => {
                            setRole(r.id);
                            setMenuOpen(false);
                          }}
                          className={`w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-surface transition ${
                            active ? "bg-surface" : ""
                          }`}
                        >
                          <span
                            className={`h-6 w-6 rounded-md bg-surface-elevated grid place-items-center ${r.accent}`}
                          >
                            <Icon className="h-3.5 w-3.5" />
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-xs font-medium">
                              {r.label}
                            </span>
                            <span className="block text-[10px] text-muted-foreground truncate">
                              {r.examples}
                            </span>
                          </span>
                          {active && (
                            <Check className="h-3.5 w-3.5 text-primary" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <button
                  onClick={() => {
                    setMenuOpen(false);
                    setManageOpen(true);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs text-muted-foreground hover:bg-surface hover:text-foreground transition border-b border-border/60"
                >
                  <Settings2 className="h-3.5 w-3.5" />
                  Hallitse kiinteistöjä ja salkkuja
                </button>

                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs text-muted-foreground hover:bg-surface hover:text-foreground transition"
                >
                  <LogOut className="h-3.5 w-3.5" />
                  Kirjaudu ulos
                </button>
              </div>
            )}
          </div>
        </div>
        </div>
      </header>
      {manageOpen && (
        <BuildingManagerOverlay onClose={() => setManageOpen(false)} />
      )}
    </>
  );
}
